import axios from 'axios';
import { 
  IPrintTemplate, 
  IPrintTemplateCreate, 
  IPrintTemplateUpdate, 
  IPrintTemplateResponse, 
  IPrintTemplatesResponse,
  ITemplateSnapshot
} from '../types/printTemplate';

/**
 * إنشاء قالب طباعة جديد
 */
export const createPrintTemplate = async (templateData: IPrintTemplateCreate): Promise<IPrintTemplate> => {
  try {
    console.log('[Client] Creating print template:', JSON.stringify(templateData, null, 2));
    
    const response = await axios.post<IPrintTemplateResponse>(
      '/api/invoices/print-templates',
      templateData
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل إنشاء قالب الطباعة');
    }
    
    return response.data.data.template;
  } catch (error: any) {
    console.error('[Client] Error creating print template:', error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * الحصول على جميع قوالب الطباعة
 */
export const getPrintTemplates = async (
  invoiceType?: string,
  language?: string
): Promise<IPrintTemplate[]> => {
  try {
    console.log(`[Client] Fetching print templates for type: ${invoiceType || 'all'}, language: ${language || 'all'}`);
    
    // إنشاء معايير التصفية
    const params: Record<string, string> = {};
    
    if (invoiceType) {
      params.invoiceType = invoiceType;
    }
    
    if (language) {
      params.displayLanguage = language; // Updated to match backend field name
    }
    
    // إضافة معلمات منع التخزين المؤقت
    const timestamp = Date.now();
    const cacheBuster = Math.random().toString(36).substring(7);
    params.t = timestamp.toString();
    params.bust = cacheBuster;
    
    const response = await axios.get<IPrintTemplatesResponse>(
      '/api/invoices/print-templates',
      {
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'x-permission-context': 'invoice'
        }
      }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل جلب قوالب الطباعة');
    }
    
    return response.data.data.templates;
  } catch (error: any) {
    console.error('[Client] Error fetching print templates:', error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * الحصول على قالب طباعة بواسطة المعرف
 */
export const getPrintTemplateById = async (id: string): Promise<IPrintTemplate> => {
  try {
    console.log(`[Client] Fetching print template with ID: ${id}`);
    
    // إضافة معلمات منع التخزين المؤقت
    const timestamp = Date.now();
    const cacheBuster = Math.random().toString(36).substring(7);
    
    const response = await axios.get<IPrintTemplateResponse>(
      `/api/invoices/print-templates/${id}`,
      {
        params: {
          t: timestamp,
          bust: cacheBuster
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل جلب قالب الطباعة');
    }
    
    return response.data.data.template;
  } catch (error: any) {
    console.error(`[Client] Error fetching print template with ID ${id}:`, error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * تحديث قالب طباعة
 */
export const updatePrintTemplate = async (
  id: string,
  updateData: IPrintTemplateUpdate
): Promise<IPrintTemplate> => {
  try {
    console.log(`[Client] Updating print template with ID: ${id}`);
    console.log('[Client] Update data:', JSON.stringify(updateData, null, 2));
    
    const response = await axios.patch<IPrintTemplateResponse>(
      `/api/invoices/print-templates/${id}`,
      updateData
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل تحديث قالب الطباعة');
    }
    
    return response.data.data.template;
  } catch (error: any) {
    console.error(`[Client] Error updating print template with ID ${id}:`, error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * حذف قالب طباعة
 */
export const deletePrintTemplate = async (id: string): Promise<{ success: boolean, message: string }> => {
  try {
    console.log(`[Client] Deleting print template with ID: ${id}`);
    
    const response = await axios.delete<{ success: boolean, message: string }>(
      `/api/invoices/print-templates/${id}`
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل حذف قالب الطباعة');
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`[Client] Error deleting print template with ID ${id}:`, error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * تعيين قالب كافتراضي
 */
export const setDefaultTemplate = async (id: string): Promise<IPrintTemplate> => {
  try {
    console.log(`[Client] Setting print template with ID: ${id} as default`);
    
    const response = await axios.patch<IPrintTemplateResponse>(
      `/api/invoices/print-templates/${id}/set-default`,
      {}
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل تعيين القالب كافتراضي');
    }
    
    return response.data.data.template;
  } catch (error: any) {
    console.error(`[Client] Error setting print template with ID ${id} as default:`, error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * الحصول على القالب الافتراضي لنوع فاتورة معين
 */
export const getDefaultTemplateByType = async (
  invoiceType: string,
  language?: string
): Promise<IPrintTemplate> => {
  try {
    console.log(`[Client] Fetching default print template for invoice type: ${invoiceType}`);
    
    // إنشاء معايير التصفية
    const params: Record<string, string> = {};
    
    if (language) {
      params.displayLanguage = language; // Updated to match backend field name
    }
    
    // إضافة معلمات منع التخزين المؤقت
    const timestamp = Date.now();
    const cacheBuster = Math.random().toString(36).substring(7);
    params.t = timestamp.toString();
    params.bust = cacheBuster;
    
    const response = await axios.get<IPrintTemplateResponse>(
      `/api/invoices/print-templates/default/${invoiceType}`,
      {
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل جلب قالب الطباعة الافتراضي');
    }
    
    return response.data.data.template;
  } catch (error: any) {
    console.error(`[Client] Error fetching default print template for invoice type ${invoiceType}:`, error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * استنساخ قالب طباعة موجود
 */
export const clonePrintTemplate = async (
  id: string,
  name: string
): Promise<IPrintTemplate> => {
  try {
    console.log(`[Client] Cloning print template with ID: ${id}`);
    
    const response = await axios.post<IPrintTemplateResponse>(
      `/api/invoices/print-templates/${id}/clone`,
      { name }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل استنساخ قالب الطباعة');
    }
    
    return response.data.data.template;
  } catch (error: any) {
    console.error(`[Client] Error cloning print template with ID ${id}:`, error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * إنشاء لقطة من تصميم القالب الحالي
 */
export const createTemplateSnapshot = async (
  id: string,
  name?: string
): Promise<{ template: IPrintTemplate, snapshot: ITemplateSnapshot }> => {
  try {
    console.log(`[Client] Creating snapshot for print template with ID: ${id}`);
    
    const response = await axios.post<{
      success: boolean,
      data: {
        template: IPrintTemplate,
        snapshot: ITemplateSnapshot
      },
      message: string
    }>(
      `/api/invoices/print-templates/${id}/snapshot`,
      { name }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل إنشاء لقطة من التصميم الحالي');
    }
    
    return response.data.data;
  } catch (error: any) {
    console.error(`[Client] Error creating snapshot for print template with ID ${id}:`, error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * الحصول على قوالب الطباعة من خلال صلاحيات إعدادات الفواتير
 * تستخدم هذه الوظيفة خصيصًا في صفحة إعدادات الفواتير
 */
export const getPrintTemplatesForInvoiceSettings = async (
  invoiceType?: string,
  language?: string
): Promise<IPrintTemplate[]> => {
  try {
    console.log(`[Client] Fetching print templates for invoice settings`);
    
    // إنشاء معايير التصفية
    const params: Record<string, string> = {};
    
    if (invoiceType) {
      params.invoiceType = invoiceType;
    }
    
    if (language) {
      params.displayLanguage = language;
    }
    
    // إضافة معلمات منع التخزين المؤقت
    const timestamp = Date.now();
    const cacheBuster = Math.random().toString(36).substring(7);
    params.t = timestamp.toString();
    params.bust = cacheBuster;
    
    const response = await axios.get<IPrintTemplatesResponse>(
      '/api/invoices/settings/templates',
      {
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل جلب قوالب الطباعة');
    }
    
    return response.data.data.templates;
  } catch (error: any) {
    console.error('[Client] Error fetching print templates for invoice settings:', error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * تصدير قوالب الطباعة المحددة أو جميع القوالب
 */
export const exportPrintTemplates = async (
  templateIds?: string[]
): Promise<{ templates: IPrintTemplate[], exportedAt: string }> => {
  try {
    console.log('[Client] Exporting print templates:', templateIds || 'all');
    
    const response = await axios.post<{
      success: boolean;
      data: {
        templates: IPrintTemplate[];
        exportedAt: string;
      };
      message: string;
    }>(
      '/api/invoices/print-templates/export',
      { templateIds }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل تصدير قوالب الطباعة');
    }
    
    return response.data.data;
  } catch (error: any) {
    console.error('[Client] Error exporting print templates:', error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * استيراد قوالب الطباعة من ملف JSON
 */
export const importPrintTemplates = async (
  templates: IPrintTemplate[]
): Promise<{ imported: number, templates: IPrintTemplate[] }> => {
  try {
    console.log('[Client] Importing print templates:', templates.length);
    
    const response = await axios.post<{
      success: boolean;
      data: {
        imported: number;
        templates: IPrintTemplate[];
      };
      message: string;
    }>(
      '/api/invoices/print-templates/import',
      { templates }
    );
    
    if (!response.data.success) {
      console.error('[Client] Server returned unsuccessful response:', response.data);
      throw new Error('فشل استيراد قوالب الطباعة');
    }
    
    return response.data.data;
  } catch (error: any) {
    console.error('[Client] Error importing print templates:', error);
    
    if (error.response) {
      console.error('[Client] Response data:', error.response.data);
      console.error('[Client] Response status:', error.response.status);
    } else if (error.request) {
      console.error('[Client] No response received from server');
    }
    
    throw error;
  }
};

/**
 * تحميل ملف قوالب للاستيراد (تحويل JSON إلى مصفوفة قوالب)
 */
export const parseImportedTemplates = (jsonString: string): IPrintTemplate[] => {
  try {
    const data = JSON.parse(jsonString);
    
    // دعم مصفوفة مباشرة أو كائن يحتوي على مصفوفة
    if (Array.isArray(data)) {
      return data;
    } else if (data.templates && Array.isArray(data.templates)) {
      return data.templates;
    } else if (data.templates) {
      return [data.templates];
    }
    
    throw new Error('صيغة ملف غير صالحة');
  } catch (error: any) {
    console.error('[Client] Error parsing template JSON:', error);
    throw new Error('ملف غير صالح. يرجى التأكد من صيغة الملف.');
  }
};

/**
 * تنزيل ملف JSON للتصدير
 */
export const downloadTemplatesAsJson = (
  templates: IPrintTemplate[],
  filename: string = 'print-templates.json'
): void => {
  const exportData = {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    templates: templates
  };
  
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};